import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiErrorDetail, fileUrl, downloadUrl } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Download, Upload, Trash2, Users, Send, Check, X as XIcon } from "lucide-react";

const EMPLOYEE_STATUSES = ["To Do", "In Progress"];
const ADMIN_STATUSES = ["To Do", "In Progress", "Review", "Done"];

export default function TaskDetailSheet({ taskId, open, onOpenChange, onUpdated, isAdmin, employeesMap = {}, clientsMap = {} }) {
  const [task, setTask] = useState(null);
  const [review, setReview] = useState("");
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [progress, setProgress] = useState(0);
  const [rejectNote, setRejectNote] = useState("");
  const [approving, setApproving] = useState(false);

  const load = async () => {
    if (!taskId) return;
    const res = await api.get(`/tasks/${taskId}`);
    setTask(res.data);
    setReview(res.data.admin_review || "");
    setProgress(res.data.progress_percent || 0);
  };

  useEffect(() => {
    if (open && taskId) load();
  }, [open, taskId]);

  const updateStatus = async (status) => {
    try {
      await api.put(`/tasks/${taskId}`, { status });
      toast.success("Task status updated");
      load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveProgress = async (value) => {
    setProgress(value);
    try {
      await api.put(`/tasks/${taskId}`, { progress_percent: value });
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const approveTask = async (action) => {
    setApproving(true);
    try {
      await api.post(`/tasks/${taskId}/approve`, { action, note: action === "reject" ? rejectNote : undefined });
      toast.success(action === "approve" ? "Task approved and marked Done" : "Task sent back for rework");
      setRejectNote("");
      load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setApproving(false);
    }
  };

  const saveReview = async () => {
    try {
      await api.put(`/tasks/${taskId}`, { admin_review: review });
      toast.success("Review saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitDeliverable = async (fileList) => {
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    try {
      await api.post(`/tasks/${taskId}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(fileList.length > 1 ? `${fileList.length} files submitted` : "Deliverable submitted");
      load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteDeliverable = async (deliverableId) => {
    try {
      await api.delete(`/tasks/${taskId}/deliverable/${deliverableId}`);
      toast.success("Deliverable removed");
      load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const postComment = async () => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      await api.post(`/tasks/${taskId}/comments`, { text: commentText.trim() });
      setCommentText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPostingComment(false);
    }
  };

  if (!task) return null;

  const statusOptions = isAdmin ? ADMIN_STATUSES : EMPLOYEE_STATUSES;
  const employeeLocked = !isAdmin && (task.status === "Review" || task.status === "Done");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="task-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading" data-testid="task-sheet-title">{task.title}</SheetTitle>
          {task.group_id && (
            <p data-testid="task-group-badge" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0EA5E9] bg-[#0EA5E9]/10 rounded-full px-2.5 py-1 mt-1 w-fit">
              <Users className="h-3 w-3" /> Group Task
            </p>
          )}
        </SheetHeader>
        <div className="mt-6 space-y-5">
          <div className="flex items-center gap-2">
            <StatusBadge value={task.priority} />
            {employeeLocked ? (
              <StatusBadge value={task.status} testId="task-sheet-status-readonly" />
            ) : (
              <Select value={task.status} onValueChange={updateStatus}>
                <SelectTrigger data-testid="task-sheet-status-select" className="w-40 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {task.approval_status && task.approval_status !== "Not Submitted" && (
              <StatusBadge value={task.approval_status} testId="task-approval-status-badge" />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-[#4B5563] uppercase tracking-wide">Progress</p>
              <span data-testid="task-progress-value" className="text-xs font-medium text-[#1C1C1E]">{progress}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={progress}
              disabled={isAdmin ? false : employeeLocked}
              onChange={(e) => setProgress(Number(e.target.value))}
              onMouseUp={(e) => saveProgress(Number(e.target.value))}
              onTouchEnd={(e) => saveProgress(Number(e.target.value))}
              onKeyUp={(e) => saveProgress(Number(e.target.value))}
              data-testid="task-progress-slider"
              className="w-full accent-[#76C121]"
            />
            <div className="h-1.5 rounded-full bg-[#F8F9FA] overflow-hidden mt-1">
              <div className="h-full rounded-full bg-[#76C121]" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {task.approval_note && task.approval_status === "Rejected" && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="task-rejection-note">
              <p className="text-xs text-red-700 uppercase tracking-wide mb-1 font-medium">Sent back for rework</p>
              <p className="text-sm text-red-700">{task.approval_note}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Assignee" value={employeesMap[task.assignee_id] || "Unassigned"} />
            <Field label="Client" value={clientsMap[task.client_id] || "—"} />
            <Field label="Deadline" value={task.deadline || "—"} />
            <Field label="Created By" value={task.created_by} />
          </div>

          <div>
            <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-1">Description</p>
            <p className="text-sm text-[#1C1C1E]">{task.description || "No description provided."}</p>
          </div>

          <div className="border-t border-[#E2E8F0] pt-4">
            <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-2">Deliverables</p>
            {(task.deliverables || []).length === 0 ? (
              <p className="text-sm text-[#4B5563] mb-2">No files submitted yet.</p>
            ) : (
              <div className="space-y-2 mb-2" data-testid="task-deliverables-list">
                {task.deliverables.map((d) => (
                  <div key={d.id} data-testid={`task-deliverable-row-${d.id}`} className="flex items-center gap-3 bg-[#F8F9FA] border border-[#E2E8F0] rounded-md px-3 py-2">
                    <a href={fileUrl(d.path)} target="_blank" rel="noreferrer" data-testid={`task-deliverable-view-link-${d.id}`} className="text-sm text-[#1C1C1E] hover:underline flex items-center gap-1.5 truncate flex-1 min-w-0">
                      <Eye className="h-4 w-4 text-[#4B5563] shrink-0" /> <span className="truncate">{d.filename}</span>
                    </a>
                    <a href={downloadUrl(d.path, d.filename)} data-testid={`task-deliverable-download-link-${d.id}`} title="Download" className="shrink-0">
                      <Download className="h-4 w-4 text-[#00823B]" />
                    </a>
                    {(isAdmin || task.status !== "Done") && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`task-deliverable-delete-button-${d.id}`} className="text-red-600 shrink-0" title="Remove file"><Trash2 className="h-4 w-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove "{d.filename}"?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteDeliverable(d.id)} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(isAdmin || task.status !== "Done") && (
              <label className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] cursor-pointer hover:underline" data-testid="task-deliverable-upload-label">
                <Upload className="h-4 w-4" /> Add file(s)
                <input type="file" multiple className="hidden" onChange={(e) => e.target.files.length > 0 && submitDeliverable(e.target.files)} />
              </label>
            )}
          </div>

          {isAdmin && (
            <div className="border-t border-[#E2E8F0] pt-4">
              <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-2">Admin Review Field</p>
              <Textarea data-testid="task-admin-review-input" value={review} onChange={(e) => setReview(e.target.value)} rows={3} placeholder="Leave feedback or approval remarks..." />
              <Button size="sm" onClick={saveReview} data-testid="task-review-save-button" className="mt-2 bg-[#00823B] hover:bg-[#006A2F] text-white">Save Review</Button>
            </div>
          )}

          {isAdmin && task.approval_status === "Pending Approval" && (
            <div className="border-t border-[#E2E8F0] pt-4 bg-[#FFFBEB] -mx-6 px-6 pb-4" data-testid="task-approval-panel">
              <p className="text-xs text-[#A16207] uppercase tracking-wide mb-2 font-medium">Deliverable Submitted — Needs Your Approval</p>
              <Textarea
                data-testid="task-reject-note-input"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={2}
                placeholder="Optional note if sending back for rework..."
                className="mb-2"
              />
              <div className="flex gap-2">
                <Button size="sm" disabled={approving} onClick={() => approveTask("approve")} data-testid="task-approve-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                  <Check className="h-4 w-4 mr-1" /> Approve & Mark Done
                </Button>
                <Button size="sm" disabled={approving} onClick={() => approveTask("reject")} data-testid="task-reject-button" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                  <XIcon className="h-4 w-4 mr-1" /> Send Back
                </Button>
              </div>
            </div>
          )}
          {!isAdmin && task.admin_review && (
            <div className="border-t border-[#E2E8F0] pt-4">
              <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-2">Admin Review</p>
              <p className="text-sm text-[#1C1C1E] bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3" data-testid="task-admin-review-readonly">{task.admin_review}</p>
            </div>
          )}

          <div className="border-t border-[#E2E8F0] pt-4">
            <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-2">Comments</p>
            <div className="space-y-2 max-h-48 overflow-y-auto mb-2" data-testid="task-comments-list">
              {(task.comments || []).length === 0 && <p className="text-sm text-[#4B5563]">No comments yet.</p>}
              {(task.comments || []).map((c, i) => (
                <div key={i} className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-2.5 text-sm">
                  <p className="text-[#1C1C1E]">{c.text}</p>
                  <p className="text-xs text-[#4B5563] mt-1">{c.author}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea data-testid="task-comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)} rows={1} placeholder="Add a comment..." className="min-h-9" />
              <Button size="icon" disabled={postingComment} onClick={postComment} data-testid="task-comment-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-[#1C1C1E] font-medium">{value}</p>
    </div>
  );
}
